import {
  ButtonView,
  InputTextView,
  View,
  ListView,
  ListItemView,
  ListItemButtonView
} from 'ckeditor5';

export function matchesQuery(field, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  const token = String(field.token || field.label || '').toLowerCase();
  if (token.includes(q)) return true;
  return (field.aliases || []).some((a) => String(a).toLowerCase().includes(q));
}

/** Every repeating-collection descriptor in the catalogue, in group order. */
export function collectionsFromGroups(groups) {
  const out = [];
  (groups || []).forEach((g) =>
    (g.fields || []).forEach((f) => {
      if (f.kind === 'collection') out.push(f);
    })
  );
  return out;
}

/** Default short badge shown on the right of a field row, keyed by its kind. */
const KIND_BADGES = {
  date: 'Date',
  clinical_assertion: 'Clinical',
  collection: 'List'
};

const clearIcon = `
<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
  <path d="M5.5 5.5 14.5 14.5M14.5 5.5 5.5 14.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
</svg>`;

export function attachTwoColumnPicker({
  locale,
  dropdownView,
  categories,
  describeField,
  title,
  subtitle,
  searchPlaceholder = 'Search all fields…'
}) {
  // Drill-down frames for the right column. Empty = browsing fields.
  let navStack = [];

  const categoryListView = new ListView(locale);
  categoryListView.extendTemplate({
    attributes: { class: ['ck-merge-field-categories'] }
  });

  const fieldListView = new ListView(locale);
  fieldListView.extendTemplate({
    attributes: { class: ['ck-merge-field-fields'] }
  });

  // Keep keyboard focus inside the panel after a drill-down re-render. Without
  // this, the just-clicked row is destroyed by the re-render, focus falls to
  // <body>, and the dropdown's close-on-blur fires — the panel would vanish
  // mid-navigation (the reported "click a field, nothing happens, it closes").
  const focusFieldList = () => {
    if (fieldListView.element) fieldListView.focus();
  };

  const searchView = new InputTextView(locale);
  searchView.set({ placeholder: searchPlaceholder });
  searchView.extendTemplate({
    attributes: { class: ['ck-merge-field-search'] }
  });

  const clearButton = new ButtonView(locale);
  clearButton.set({
    label: 'Clear search',
    tooltip: true,
    withText: false,
    icon: clearIcon,
    isVisible: false,
    class: 'ck-merge-field-search-clear'
  });

  let selectedKey = categories.length ? categories[0].key : null;
  const categoryButtons = [];
  const currentCategory = () =>
    categories.find((c) => c.key === selectedKey) || categories[0] || null;

  const makeHeaderListItem = (label) => {
    const listItemView = new ListItemView(locale);
    const buttonView = new ListItemButtonView(locale);
    buttonView.set({
      label,
      withText: true,
      isEnabled: false,
      class: 'ck-merge-field-group-header'
    });
    listItemView.children.add(buttonView);
    return listItemView;
  };

  const makeEmptyListItem = (label) => {
    const listItemView = new ListItemView(locale);
    const buttonView = new ListItemButtonView(locale);
    buttonView.set({
      label,
      withText: true,
      isEnabled: false,
      class: 'ck-merge-field-empty'
    });
    listItemView.children.add(buttonView);
    return listItemView;
  };

  const makeBackRow = (label, onBack) => {
    const listItemView = new ListItemView(locale);
    const buttonView = new ListItemButtonView(locale);
    buttonView.set({ label, withText: true, class: 'ck-merge-field-back' });
    buttonView.on('execute', onBack);
    listItemView.children.add(buttonView);
    return listItemView;
  };

  const makeSectionHeaderRow = (section, onToggle) => {
    const listItemView = new ListItemView(locale);
    const buttonView = new ListItemButtonView(locale);
    buttonView.set({
      label: `${section.expanded ? '▾' : '▸'} ${section.label}`,
      withText: true,
      class: 'ck-merge-field-section-header'
    });
    buttonView.on('execute', onToggle);
    listItemView.children.add(buttonView);
    return listItemView;
  };

  const handleNode = (node) => {
    if (node.sections && node.sections.length) {
      navStack.push({
        title: node.title || node.label,
        sections: node.sections
      });
      // eslint-disable-next-line no-use-before-define -- mutual recursion with renderRight
      renderRight();
      focusFieldList();
    } else if (node.children && node.children.length) {
      navStack.push({ title: node.title || node.label, nodes: node.children });
      // eslint-disable-next-line no-use-before-define -- mutual recursion with renderRight
      renderRight();
      focusFieldList();
    } else if (typeof node.onExecute === 'function') {
      node.onExecute();
    }
  };

  const makeNodeListItem = (node) => {
    const listItemView = new ListItemView(locale);
    const buttonView = new ListItemButtonView(locale);
    const isBranch = !!(
      (node.children && node.children.length) ||
      (node.sections && node.sections.length)
    );
    const classes = node.classes ? node.classes.slice() : [];
    if (isBranch) classes.push('ck-merge-field-branch');
    buttonView.set({
      label: node.label,
      withText: true,
      class: classes.join(' ') || undefined
    });
    const attributes = {};
    if (isBranch) {
      attributes['data-branch'] = 'true';
    } else {
      if (node.kind) attributes['data-kind'] = node.kind;
      if (node.badge) attributes['data-badge'] = node.badge;
    }
    if (Object.keys(attributes).length) {
      buttonView.extendTemplate({ attributes });
    }
    buttonView.on('execute', () => handleNode(node));
    listItemView.children.add(buttonView);
    return listItemView;
  };

  /** Build a level-1 right-column node from a catalogue field descriptor. */
  const fieldToNode = (field, category) => {
    const desc = describeField(field, category) || {};
    const badge =
      desc.badge !== undefined ? desc.badge : KIND_BADGES[field.kind] || '';
    const baseClasses = desc.classes || [];
    const classes = category.isLoop
      ? [...baseClasses, 'ck-merge-field-loop-item']
      : baseClasses;
    return {
      label: desc.label,
      title: desc.title,
      classes,
      kind: field.kind,
      badge,
      children: desc.children,
      sections: desc.sections,
      onExecute: desc.onExecute
    };
  };

  const renderFieldsForCategory = (category) => {
    fieldListView.items.clear();
    if (!category) {
      fieldListView.items.add(makeEmptyListItem('No fields available'));
      return;
    }
    category.fields.forEach((f) =>
      fieldListView.items.add(makeNodeListItem(fieldToNode(f, category)))
    );
  };

  const renderFieldsForQuery = (query) => {
    fieldListView.items.clear();
    categories.forEach((category) => {
      const matches = category.fields.filter((f) => matchesQuery(f, query));
      if (!matches.length) return;
      fieldListView.items.add(makeHeaderListItem(category.label));
      matches.forEach((f) =>
        fieldListView.items.add(makeNodeListItem(fieldToNode(f, category)))
      );
    });
    if (!fieldListView.items.length) {
      fieldListView.items.add(makeEmptyListItem('No matching fields'));
    }
  };

  // Single entry point for the right column: a drill-down frame if any, else
  // the current search results or the selected category's fields.
  function renderRight() {
    if (navStack.length) {
      const frame = navStack[navStack.length - 1];
      fieldListView.items.clear();
      fieldListView.items.add(
        makeBackRow('Back', () => {
          navStack.pop();
          renderRight();
          focusFieldList();
        })
      );
      if (frame.sections) {
        fieldListView.items.add(makeHeaderListItem(frame.title));
        frame.sections.forEach((section) => {
          fieldListView.items.add(
            makeSectionHeaderRow(section, () => {
              frame.sections.forEach((s) => {
                s.expanded = s === section;
              });
              renderRight();
              focusFieldList();
            })
          );
          if (section.expanded) {
            (section.nodes || []).forEach((node) =>
              fieldListView.items.add(makeNodeListItem(node))
            );
          }
        });
        return;
      }
      fieldListView.items.add(makeHeaderListItem(frame.title));
      frame.nodes.forEach((node) =>
        fieldListView.items.add(makeNodeListItem(node))
      );
      return;
    }
    const query = searchView.element ? searchView.element.value.trim() : '';
    if (query) renderFieldsForQuery(query);
    else renderFieldsForCategory(currentCategory());
  }

  const selectCategory = (category) => {
    selectedKey = category.key;
    categoryButtons.forEach(({ key, buttonView }) => {
      buttonView.isOn = key === selectedKey;
    });
    navStack = [];
    if (searchView.element) searchView.element.value = '';
    clearButton.isVisible = false;
    renderRight();
  };

  categories.forEach((category) => {
    const listItemView = new ListItemView(locale);
    const buttonView = new ListItemButtonView(locale);
    buttonView.set({
      label: category.label,
      withText: true,
      isToggleable: true,
      isOn: category.key === selectedKey,
      class: category.isLoop ? 'ck-merge-field-loop-item' : undefined
    });
    buttonView.extendTemplate({
      attributes: { 'data-count': String((category.fields || []).length) }
    });
    buttonView.on('execute', () => selectCategory(category));
    listItemView.children.add(buttonView);
    categoryListView.items.add(listItemView);
    categoryButtons.push({ key: category.key, buttonView });
  });

  const runSearch = (query) => {
    navStack = [];
    clearButton.isVisible = !!query;
    renderRight();
  };

  searchView.on('input', () => {
    runSearch(searchView.element.value.trim());
  });

  clearButton.on('execute', () => {
    if (searchView.element) searchView.element.value = '';
    runSearch('');
    if (searchView.element) searchView.element.focus();
  });

  renderRight();

  const searchWrapperView = new View(locale);
  searchWrapperView.setTemplate({
    tag: 'div',
    attributes: { class: ['ck-merge-field-search-wrap'] },
    children: [searchView, clearButton]
  });

  const columnsView = new View(locale);
  columnsView.setTemplate({
    tag: 'div',
    attributes: { class: ['ck-merge-field-columns'] },
    children: [categoryListView, fieldListView]
  });

  if (title) {
    const headerChildren = [
      {
        tag: 'span',
        attributes: { class: ['ck-merge-field-header-title'] },
        children: [{ text: title }]
      }
    ];
    if (subtitle) {
      headerChildren.push({
        tag: 'span',
        attributes: { class: ['ck-merge-field-header-subtitle'] },
        children: [{ text: subtitle }]
      });
    }
    const headerView = new View(locale);
    headerView.setTemplate({
      tag: 'div',
      attributes: { class: ['ck-merge-field-header'] },
      children: headerChildren
    });
    dropdownView.panelView.children.add(headerView);
  }

  dropdownView.panelView.children.add(searchWrapperView);
  dropdownView.panelView.children.add(columnsView);

  dropdownView.on('change:isOpen', (evt, name, isOpen) => {
    if (isOpen) return;
    navStack = [];
    if (searchView.element) {
      searchView.reset();
      clearButton.isVisible = false;
    }
    renderRight();
  });
}
